import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import {
  useBranding,
  useSaveUiSettings,
  useUiSettings,
  useUserProfile,
} from '@/features/auth/api';
import { serializeTheme, useThemeStore } from '@/lib/theme/store';
import type { SerializedTheme } from '@/lib/theme/store';
import { Header } from './Header';
import { Sidebar, TopBar } from './Sidebar';
import { SidebarDocker, SidebarDragHandle } from './SidebarDocker';

/** Debounce window between a theme-store change and the server save. */
const SAVE_DEBOUNCE_MS = 1500;

/**
 * Parse a JSON blob safely. Returns `null` on any error so callers can keep
 * existing state rather than blowing up on corrupted persisted prefs.
 */
function safeParseTheme(json: string | null | undefined): Partial<SerializedTheme> | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === 'object') return parsed as Partial<SerializedTheme>;
    return null;
  } catch {
    return null;
  }
}

/**
 * Authenticated shell. The sidebar placement (`left` / `right` / `top`) is
 * read from the theme store; the layout reorders accordingly. The whole
 * shell is wrapped in <SidebarDocker> so the drag handle in the sidebar
 * can dock to any edge.
 */
export function AppShell() {
  const placement = useThemeStore((s) => s.sidebarPlacement);
  const hydrateFromServer = useThemeStore((s) => s.hydrateFromServer);
  const setHydrating = useThemeStore((s) => s.setHydrating);
  const branding = useBranding();
  const uiSettings = useUiSettings();
  const saveUiSettings = useSaveUiSettings();
  const profile = useUserProfile();

  // Reflect the branded company name in the document title.
  useEffect(() => {
    if (branding.data?.company) {
      document.title = branding.data.company;
    }
  }, [branding.data?.company]);

  /* ---- Hydrate the theme store on login ---------------------------------
   *
   * Two layers, applied in order so later overlays win:
   *   1. Per-domain `uiDefaults` from the branding endpoint.
   *   2. Per-user `uiSettings` from /api/obd/user/{id}/ui/settings.
   *
   * We toggle `isHydrating` while either query is loading so the debounced
   * save subscriber below doesn't echo the hydration straight back to the
   * server. */
  useEffect(() => {
    setHydrating(uiSettings.isLoading || branding.isLoading);
  }, [uiSettings.isLoading, branding.isLoading, setHydrating]);

  useEffect(() => {
    const defaults = safeParseTheme(branding.data?.uiDefaults);
    if (defaults) hydrateFromServer(defaults);
  }, [branding.data?.uiDefaults, hydrateFromServer]);

  useEffect(() => {
    const overlay = safeParseTheme(uiSettings.data?.json);
    if (overlay) hydrateFromServer(overlay);
  }, [uiSettings.data?.json, hydrateFromServer]);

  /* ---- Debounced save on every theme-store change ---------------------- */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    // Subscribe outside React's render cycle so we don't trigger a re-render
    // on every keystroke-equivalent setter. The closure captures the latest
    // mutation handle via the ref pattern.
    const unsub = useThemeStore.subscribe((state, prev) => {
      if (state.isHydrating) return;
      // Skip if the persisted slice didn't actually change (e.g. only
      // isHydrating flipped).
      const next = JSON.stringify(serializeTheme(state));
      const prevSerialized = JSON.stringify(serializeTheme(prev));
      if (next === prevSerialized) return;
      if (next === lastSentRef.current) return;

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lastSentRef.current = next;
        saveUiSettings.mutate(next);
      }, SAVE_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [saveUiSettings]);

  const brand = {
    logoUrl: branding.data?.logoUrl ?? null,
    company: branding.data?.company ?? null,
  };

  const isHorizontal = placement === 'top';
  const isRight = placement === 'right';

  /*
   * Layout strategy — fixed viewport height with no page-level scrolling.
   * The root container is h-screen + overflow-hidden so children (mainly
   * the DataTable) can host their own internal scroll regions and pin
   * sticky headers reliably. Pages that need scrollable content (e.g.
   * UserProfilePage) wrap themselves in an overflow-y-auto container.
   *
   * The previous min-h-screen layout let the page itself scroll, which
   * meant sticky table headers (positioned relative to the inner
   * overflow-x container) could not pin to the viewport during page
   * scroll. Switching to fixed-viewport + internal scroll fixes that
   * once and for all.
   */
  return (
    <SidebarDocker>
      <div className="flex h-screen overflow-hidden bg-background">
        {!isHorizontal && !isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <Header
            username={profile.data?.username ?? profile.data?.name ?? ''}
            voiceCredits={profile.data?.credits}
            ttsCredits={profile.data?.ttsCredits}
          />
          {isHorizontal && <TopBar brand={brand} dragHandle={<SidebarDragHandle />} />}
          <main className="flex min-h-0 flex-1 flex-col">
            <Outlet />
          </main>
        </div>
        {!isHorizontal && isRight && (
          <Sidebar brand={brand} showDragHandle dragHandle={<SidebarDragHandle />} />
        )}
      </div>
    </SidebarDocker>
  );
}
